// §10 Step 5 — Identity verification. Four capture tiles, then the checking
// panel (three lines tick green every 0.65s). Mock: nothing is verified.
// Documents use a file picker; the selfie tile opens the device camera
// (getUserMedia → browser asks for camera permission) with live preview.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Modal } from 'react-bootstrap';
import OnboardingLayout from './components/OnboardingLayout';
import StepGuard from './components/StepGuard';
import { useOnboarding } from '@/context/OnboardingContext';
import { OnboardingApiError, uploadDocuments } from '@/api/onboarding';
import type { IdentityCaptures } from '@/types/onboarding.types';
import { compressDrawable, compressImage, photoBudgetBytes } from '@/utils/imageCompress';
import './onboarding.css';

type TileKey = keyof IdentityCaptures;

const TILES: { key: TileKey; label: string; capture: 'environment' | 'user' }[] = [
  { key: 'aadhaarFront', label: 'Aadhaar front', capture: 'environment' },
  { key: 'aadhaarBack', label: 'Aadhaar back', capture: 'environment' },
  { key: 'panCard', label: 'PAN card', capture: 'environment' },
  { key: 'selfie', label: 'Selfie', capture: 'user' },
];

const CHECK_LINES = ['Document quality', 'Details match your form', 'Face match'];

// All four photos go up in one request and the server takes at most 1 MB per
// request, so each photo is shrunk to about 220 KB when it is taken.
const PHOTO_BUDGET_BYTES = photoBudgetBytes(TILES.length);

const IdentityVerificationForm = () => {
  const navigate = useNavigate();
  const { draft, updateIdentity, markIdentityVerified } = useOnboarding();
  const [checking, setChecking] = useState(false);
  const [passedLines, setPassedLines] = useState(0);
  const [pickerError, setPickerError] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const inputRefs = useRef<Record<TileKey, HTMLInputElement | null>>({
    aadhaarFront: null,
    aadhaarBack: null,
    panCard: null,
    selfie: null,
  });

  const allCaptured = TILES.every((t) => draft.identity[t.key]);

  const closeCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraOpen(false);
  };

  // Start the camera when the selfie modal opens. getUserMedia triggers the
  // browser's "allow camera" permission prompt.
  useEffect(() => {
    if (!cameraOpen) return;
    let cancelled = false;

    if (!navigator.mediaDevices?.getUserMedia) {
      setPickerError('Camera is not available in this browser. Use a browser with camera support.');
      setCameraOpen(false);
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user' }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => undefined);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setPickerError('Camera access was denied. Please allow camera access to take your selfie.');
        setCameraOpen(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cameraOpen]);

  // Stop the camera if the step unmounts while the modal is open.
  useEffect(
    () => () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
    },
    [],
  );

  const captureSelfie = () => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) return;
    // Mirror so the photo matches the mirrored preview.
    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(video, 0, 0);
    try {
      const photo = compressDrawable(canvas, canvas.width, canvas.height, PHOTO_BUDGET_BYTES);
      updateIdentity({ selfie: photo.dataUrl });
      setPickerError('');
    } catch {
      setPickerError('Could not save your selfie. Please try again.');
    }
    closeCamera();
  };

  const handleFile = async (key: TileKey, file: File | undefined) => {
    if (!file) return;
    try {
      const photo = await compressImage(file, PHOTO_BUDGET_BYTES);
      updateIdentity({ [key]: photo.dataUrl });
      setPickerError('');
    } catch {
      setPickerError('Could not read this photo. Please take it again or choose another photo.');
    }
  };

  const handleContinue = async () => {
    if (!allCaptured || checking) return;
    setChecking(true);
    setPassedLines(0);
    setPickerError('');
    try {
      // Upload all four documents to the backend in one request.
      await uploadDocuments({
        aadhaarFront: draft.identity.aadhaarFront as string,
        aadhaarBack: draft.identity.aadhaarBack as string,
        panCard: draft.identity.panCard as string,
        selfie: draft.identity.selfie as string,
      });
      // Show the checking animation, then advance.
      CHECK_LINES.forEach((_, i) => {
        setTimeout(() => setPassedLines(i + 1), 650 * (i + 1));
      });
      setTimeout(() => {
        markIdentityVerified();
        navigate('/open-account/signature-capture');
      }, 650 * CHECK_LINES.length + 500);
    } catch (err) {
      setChecking(false);
      setPassedLines(0);
      setPickerError(
        err instanceof OnboardingApiError ? err.message : 'Could not upload your documents. Please try again.'
      );
    }
  };

  if (checking) {
    return (
      <OnboardingLayout step={5} heading="Verify your identity" backTo="/open-account/email-verification">
        <div className="d-flex flex-column align-items-center justify-content-center py-5">
          {passedLines < CHECK_LINES.length && (
            <span className="spinner-border mb-4" style={{ color: 'var(--nova-blue)' }} aria-hidden="true" />
          )}
          <div className="text-start">
            {CHECK_LINES.map((line, i) => (
              <div key={line} className={`nova-ob-checkline ${i < passedLines ? 'done' : ''}`}>
                <span aria-hidden="true">{i < passedLines ? '✓' : '○'}</span>
                <span>{line}</span>
              </div>
            ))}
          </div>
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout step={5} heading="Verify your identity" backTo="/open-account/email-verification">
      <p>Take clear photos of your documents. Make sure all four corners are visible and there is no glare.</p>

      {pickerError && (
        <div className="alert alert-danger py-2" role="alert">{pickerError}</div>
      )}

      <div className="row g-3">
        {TILES.map((tile) => {
          const image = draft.identity[tile.key];
          return (
            <div className="col-6" key={tile.key}>
              <button
                type="button"
                className={`nova-ob-tile ${image ? 'filled' : ''}`}
                onClick={() =>
                  tile.key === 'selfie'
                    ? setCameraOpen(true)
                    : inputRefs.current[tile.key]?.click()
                }
              >
                {image ? (
                  <>
                    <img src={image} alt={tile.label} />
                    <span className="text-success fw-semibold">✓ {tile.label}</span>
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                      {tile.key === 'selfie' ? 'Retake' : 'Reupload'}
                    </span>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: '1.75rem' }} aria-hidden="true">📷</span>
                    <span className="fw-semibold">{tile.label}</span>
                  </>
                )}
              </button>
              {tile.key !== 'selfie' && (
                <input
                  ref={(el) => {
                    inputRefs.current[tile.key] = el;
                  }}
                  type="file"
                  accept="image/*"
                  capture={tile.capture}
                  hidden
                  onChange={(e) => handleFile(tile.key, e.target.files?.[0])}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="nova-ob-footer">
        <div className="nova-ob-footer-inner">
          <button
            type="button"
            className="btn btn-primary btn-lg w-100 fw-semibold nova-btn"
            disabled={!allCaptured}
            onClick={handleContinue}
          >
            Continue
          </button>
        </div>
      </div>

      {/* Selfie camera — live preview + capture (§10) */}
      <Modal show={cameraOpen} onHide={closeCamera} centered>
        <Modal.Header closeButton>
          <Modal.Title>Take a selfie</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              borderRadius: 12,
              background: '#000',
              transform: 'scaleX(-1)',
            }}
          />
          <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.85rem' }}>
            Centre your face in the frame, then tap Capture.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={closeCamera}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary nova-btn" onClick={captureSelfie}>
            Capture
          </button>
        </Modal.Footer>
      </Modal>
    </OnboardingLayout>
  );
};

const IdentityVerification = () => (
  <StepGuard requires={(d) => d.emailVerified}>
    <IdentityVerificationForm />
  </StepGuard>
);

export default IdentityVerification;
